import apiService from './apiService';

/**
 * Kanban board API wrapper. Mirrors the shape of expenseService.js — every
 * call is a thin HTTP wrapper, no client-side caching. Components are
 * expected to refetch on window events (omnis:board-* / omnis:ticket-*)
 * fired by documentService's SSE parser when AI tools mutate boards.
 */
export const boardService = {
    // ── Boards ────────────────────────────────────────────────────────
    async listBoards({ includeArchived = false } = {}) {
        const qs = includeArchived ? '?include_archived=true' : '';
        return apiService.get(`/api/boards${qs}`);
    },
    async getBoard(boardId) {
        return apiService.get(`/api/boards/${boardId}`);
    },
    async getBoardFull(boardId) {
        return apiService.get(`/api/boards/${boardId}/full`);
    },
    async createBoard({ name, description, defaultColumns } = {}) {
        const body = { name };
        if (description !== undefined) body.description = description;
        if (defaultColumns !== undefined) body.default_columns = defaultColumns;
        return apiService.post('/api/boards', body);
    },
    async updateBoard(boardId, updates) {
        return apiService.patch(`/api/boards/${boardId}`, updates);
    },
    async deleteBoard(boardId) {
        return apiService.delete(`/api/boards/${boardId}`);
    },

    // ── Members ───────────────────────────────────────────────────────
    async listMembers(boardId) {
        return apiService.get(`/api/boards/${boardId}/members`);
    },
    // Invite by invitee_user_id (Members UI management #194 — the Shared Space
    // picker resolves a network contact / team member to a real user id) or by
    // email (back-compat). Undefined keys are dropped by JSON.stringify.
    async inviteMember(boardId, { email, invitee_user_id, role = 'member' } = {}) {
        return apiService.post(`/api/boards/${boardId}/members`, { email, invitee_user_id, role });
    },
    async updateMember(boardId, userId, { role }) {
        return apiService.patch(`/api/boards/${boardId}/members/${userId}`, { role });
    },
    async removeMember(boardId, userId) {
        return apiService.delete(`/api/boards/${boardId}/members/${userId}`);
    },
    // #194 Team→Board shares: remove a whole TEAM from this board — every member
    // that team granted loses access at once. Asset-side + owner-only (the board
    // owner isn't necessarily a member of the granting team, so the member-only
    // team-side unshare can't be their route out).
    // → { unshared, kind:'board', members_removed }
    async removeTeamShare(boardId, teamId) {
        return apiService.delete(`/api/boards/${boardId}/team-shares/${teamId}`);
    },

    // ── Labels ────────────────────────────────────────────────────────
    async createLabel(boardId, { name, color }) {
        return apiService.post(`/api/boards/${boardId}/labels`, { name, color });
    },
    async updateLabel(labelId, updates) {
        return apiService.patch(`/api/boards/labels/${labelId}`, updates);
    },
    async deleteLabel(labelId) {
        return apiService.delete(`/api/boards/labels/${labelId}`);
    },

    // ── Columns ───────────────────────────────────────────────────────
    async createColumn(boardId, { name, color, position } = {}) {
        const body = { name };
        if (position !== undefined) body.position = position;
        return apiService.post(`/api/boards/${boardId}/columns`, body);
    },
    async updateColumn(columnId, updates) {
        return apiService.patch(`/api/boards/columns/${columnId}`, updates);
    },
    async deleteColumn(columnId) {
        return apiService.delete(`/api/boards/columns/${columnId}`);
    },
    async reorderColumns(boardId, order) {
        return apiService.post(`/api/boards/columns/reorder`, { board_id: boardId, order });
    },
    // Move a column (with all its tickets) to a different board at a position (#110).
    async moveColumn(columnId, targetBoardId, position) {
        return apiService.post(`/api/boards/columns/${columnId}/move`, { target_board_id: targetBoardId, position });
    },

    // ── Tickets ───────────────────────────────────────────────────────
    async createTicket(columnId, data) {
        // data: { title, description?, due_date?, label_ids?, assignee_ids? }
        return apiService.post(`/api/boards/columns/${columnId}/tickets`, data);
    },
    async getTicket(ticketId) {
        return apiService.get(`/api/boards/tickets/${ticketId}`);
    },
    async updateTicket(ticketId, updates) {
        // updates: any subset of { title, description, column_id, position,
        //   cover_document_id, due_date, label_ids, assignee_ids, is_archived,
        //   is_completed (PAI Tick #213) }
        return apiService.patch(`/api/boards/tickets/${ticketId}`, updates);
    },
    async deleteTicket(ticketId) {
        return apiService.delete(`/api/boards/tickets/${ticketId}`);
    },
    async reorderTickets(moves) {
        // moves: [{ ticket_id, column_id, position }, ...]
        return apiService.post(`/api/boards/tickets/reorder`, { moves });
    },
    // Move a ticket to a column at a precise position — INCLUDING a column on a
    // DIFFERENT board (Manual Ticket movement #136). position is 0-based.
    async moveTicket(ticketId, targetColumnId, position) {
        return apiService.post(`/api/boards/tickets/${ticketId}/move`, {
            target_column_id: targetColumnId,
            position,
        });
    },

    // ── Comments ──────────────────────────────────────────────────────
    async addComment(ticketId, content) {
        return apiService.post(`/api/boards/tickets/${ticketId}/comments`, { content });
    },
    async updateComment(commentId, content) {
        return apiService.patch(`/api/boards/comments/${commentId}`, { content });
    },
    async deleteComment(commentId) {
        return apiService.delete(`/api/boards/comments/${commentId}`);
    },

    // ── Attachments ───────────────────────────────────────────────────
    async addAttachment(ticketId, { documentId, setAsCover = false }) {
        return apiService.post(`/api/boards/tickets/${ticketId}/attachments`, {
            document_id: documentId,
            set_as_cover: setAsCover,
        });
    },
    async removeAttachment(attachmentId) {
        return apiService.delete(`/api/boards/attachments/${attachmentId}`);
    },
};

export default boardService;
